import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
  type ThunkDispatch,
  type UnknownAction,
} from '@reduxjs/toolkit';

import { authApi } from '@/api/auth.api';

import { clearAuthStorage } from '@/api/client';
import { queryClient } from '@/lib/query/queryClient';
import { beginSignOut, endSignOut } from '@/utils/authTransition';
import { clearLastAllowedPath, seedLastAllowedPath } from '@/utils/routeAccess';
import { resolveAccessibleLandingPath } from '@/utils/pageAccessEngine';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { enabledHeadModuleCodes, getModuleEntryByLoginPath } from '@/config/moduleEntries';

import { getOrCreateDeviceId } from '@/core/auth/deviceId';
import { clearWorkspaceScopeStorage } from '@/utils/workspaceScope';

import { STORAGE_KEYS } from '@/constants/storage';

import type { ErpModule } from '@/api/platform.api';
import {
  isLauncherAccessibleModule,
  isOrgOwner,
  resolveDefaultAccessibleWorkspaceModule,
} from '@/utils/moduleAccess';

import type { AddApplicationRequest, OrganizationRegisterRequest } from '@/api/auth.api';
import type { AuthUser, LoginRequest, LoginResponse } from '@/types/auth';
import type { SessionInitResponse } from '@/types/session';

import {
  clearEntryModuleStorage,
  clearLastModuleSignIn,
  persistEntryModule,
  persistWorkspaceModule,
  markWorkspaceModuleUsed,
  readStoredEntryModule,
  readStoredWorkspaceModule,
  clearUsedWorkspaceModules,
} from '@/utils/moduleScope';
import { platformApi } from '@/api/platform.api';
import { bootstrapSession, clearSession, sessionToLoginResponse } from '@/store/sessionSlice';
import { applyOAuthAvatar } from '@/utils/userAvatarStorage';
import { setSelectedCompany, setSelectedStore } from '@/store/appSlice';

type AuthThunkDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

const SESSION_REFRESH_FULFILLED = 'session/refresh/fulfilled';

const inflightSsoByToken = new Map<string, Promise<LoginResponse>>();

interface AuthState {
  user: AuthUser | null;
  enabledFeatures: string[];
  enabledModules: string[];
  accessibleModules: string[];
  entryModule: ErpModule | null;
  workspaceModule: ErpModule | null;
  mfaEnabled: boolean;
  isAuthenticated: boolean;
  loading: boolean;
  initialized: boolean;
  forcePasswordChange: boolean;
}

function loginToUser(data: LoginResponse): AuthUser {
  return {
    userId: data.userId,
    userUuid: data.userUuid,
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    organizationId: data.organizationId,
    organizationName: data.organizationName ?? null,
    masterAdmin: data.masterAdmin,
    permissions: data.permissions ?? [],
    roleCode: data.roleCode ?? null,
    roleName: data.roleName ?? null,
    storeId: data.storeId ?? null,
    profilePictureUrl: data.profilePictureUrl ?? null,
  };
}

function resolveEntryModule(data: LoginResponse): ErpModule | null {
  if (data.entryModule) return data.entryModule as ErpModule;
  return null;
}

const initialState: AuthState = {
  user: null,
  enabledFeatures: [],
  enabledModules: [],
  accessibleModules: [],
  entryModule: readStoredEntryModule(),
  workspaceModule: readStoredWorkspaceModule(),
  mfaEnabled: false,
  isAuthenticated: false,
  loading: false,
  initialized: false,
  forcePasswordChange: false,
};

type ApplyLoginOptions = {
  /** True when rehydrating an existing session (page refresh), not a fresh sign-in. */
  restore?: boolean;
};

function resolveLoginPathModule(): ErpModule | null {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname.split('?')[0].replace(/\/+$/, '') || '/';
  return getModuleEntryByLoginPath(path)?.erpModule ?? null;
}

type WorkspaceModuleContext = {
  enabledModules: string[];
  accessibleModules: string[];
  permissions: string[];
  roleCode?: string | null;
};

function canUseWorkspaceModule(
  module: ErpModule | null,
  ctx: WorkspaceModuleContext,
): module is ErpModule {
  return isLauncherAccessibleModule(
    module,
    ctx.enabledModules,
    ctx.permissions,
    ctx.accessibleModules,
    ctx.roleCode,
  );
}

function resolveWorkspaceModule(
  owner: boolean,
  resolvedEntry: ErpModule | null,
  ctx: WorkspaceModuleContext,
  restore: boolean,
): ErpModule | null {
  const pendingRaw = sessionStorage.getItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
  const pendingModule = pendingRaw ? (pendingRaw as ErpModule) : null;
  const loginPathModule = resolveLoginPathModule();
  const defaultModule = resolveDefaultAccessibleWorkspaceModule(
    ctx.enabledModules,
    ctx.permissions,
    ctx.accessibleModules,
    ctx.roleCode,
  );

  if (owner) {
    if (restore) {
      const fromPath =
        typeof window !== 'undefined' ? resolveModuleFromPath(window.location.pathname) : null;
      const stored = readStoredWorkspaceModule();
      for (const candidate of [loginPathModule, pendingModule, fromPath, stored, resolvedEntry]) {
        if (canUseWorkspaceModule(candidate, ctx)) {
          return candidate;
        }
      }
      return defaultModule;
    }
    // Prefer the login URL / pending module so /hr/login never defaults owners to POS.
    if (canUseWorkspaceModule(loginPathModule, ctx)) return loginPathModule;
    if (canUseWorkspaceModule(pendingModule, ctx)) return pendingModule;
    if (canUseWorkspaceModule(resolvedEntry, ctx)) return resolvedEntry;
    return defaultModule;
  }

  if (restore) {
    const fromPath =
      typeof window !== 'undefined' ? resolveModuleFromPath(window.location.pathname) : null;
    const stored = readStoredWorkspaceModule();
    for (const candidate of [loginPathModule, fromPath, pendingModule, resolvedEntry, stored]) {
      if (canUseWorkspaceModule(candidate, ctx)) {
        return candidate;
      }
    }
    return defaultModule;
  }

  if (canUseWorkspaceModule(loginPathModule, ctx)) return loginPathModule;
  if (canUseWorkspaceModule(pendingModule, ctx)) return pendingModule;
  if (canUseWorkspaceModule(resolvedEntry, ctx)) return resolvedEntry;
  return defaultModule;
}

function applySessionModuleLists(state: AuthState, data: LoginResponse) {
  state.enabledFeatures = data.enabledFeatures ?? [];
  state.enabledModules = enabledHeadModuleCodes(data.enabledModules ?? []);
  state.accessibleModules = data.accessibleModules ?? [];
}

function patchUserFromSessionRefresh(state: AuthState, data: LoginResponse) {
  if (!state.user) return;
  state.user.roleCode = data.roleCode ?? state.user.roleCode;
  state.user.roleName = data.roleName ?? state.user.roleName;
  state.user.permissions = data.permissions ?? state.user.permissions;
  state.user.firstName = data.firstName ?? state.user.firstName;
  state.user.lastName = data.lastName ?? state.user.lastName;
  state.user.organizationName = data.organizationName ?? state.user.organizationName;
  if (data.profilePictureUrl) {
    state.user.profilePictureUrl = data.profilePictureUrl;
  }
}

function applyLoginPayload(state: AuthState, data: LoginResponse, options?: ApplyLoginOptions) {
  const restore = options?.restore === true;
  state.user = loginToUser(data);
  state.isAuthenticated = true;
  applySessionModuleLists(state, data);
  state.mfaEnabled = data.mfaEnabled === true;
  state.forcePasswordChange = data.forcePasswordChange ?? false;

  if (data.masterAdmin) {
    state.entryModule = null;
    state.workspaceModule = null;
    clearEntryModuleStorage();
    clearLastModuleSignIn();
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
    if (!restore) {
      seedLastAllowedPath('/master');
    }
    return;
  }

  const owner = state.user && isOrgOwner(state.user.permissions, state.user.roleCode);
  const resolvedEntry = resolveEntryModule(data);
  const workspaceCtx: WorkspaceModuleContext = {
    enabledModules: state.enabledModules,
    accessibleModules: state.accessibleModules,
    permissions: state.user?.permissions ?? [],
    roleCode: state.user?.roleCode,
  };
  const resolvedWorkspace = resolveWorkspaceModule(owner, resolvedEntry, workspaceCtx, restore);
  state.entryModule = owner ? null : resolvedWorkspace;
  state.workspaceModule = resolvedWorkspace;
  persistEntryModule(state.entryModule);
  persistWorkspaceModule(state.workspaceModule);
  markWorkspaceModuleUsed(state.workspaceModule ?? state.entryModule);
  sessionStorage.removeItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
  if (!restore) {
    const landing = resolveAccessibleLandingPath(
      {
        permissions: state.user?.permissions ?? [],
        accessibleModules: state.accessibleModules,
        entryModule: state.workspaceModule ?? state.entryModule,
        enabledModules: state.enabledModules,
        roleCode: state.user?.roleCode,
      },
      state.workspaceModule ?? state.entryModule,
    );
    seedLastAllowedPath(landing);
  }
}

export const restoreSession = bootstrapSession;

function hasStoredProfilePicture(url?: string | null): boolean {
  return Boolean(url?.trim());
}

async function finishAuthSession(
  dispatch: AuthThunkDispatch,
  data: LoginResponse,
  oauthPictureUrl?: string,
) {
  const serverPicture = hasStoredProfilePicture(data.profilePictureUrl) ? data.profilePictureUrl!.trim() : null;
  const profilePictureUrl = serverPicture ?? (oauthPictureUrl?.trim() || null);
  const payload: LoginResponse = profilePictureUrl
    ? { ...data, profilePictureUrl }
    : data;
  dispatch({ type: 'auth/hydrateFromLogin', payload });
  if (payload.userUuid && profilePictureUrl && !serverPicture) {
    applyOAuthAvatar(payload.userUuid, profilePictureUrl);
  }
  dispatch(clearSession());
  await dispatch(bootstrapSession({ force: true })).unwrap();
  queryClient.invalidateQueries({ queryKey: ['hr', 'ess'] });
}

/** Clear any stale browser session before signup so logout cannot revoke cookies issued by register. */
async function clearPriorSessionBeforeSignup(dispatch: AuthThunkDispatch) {
  clearWorkspaceScopeStorage();
  clearAuthStorage();
  dispatch(setSelectedCompany(null));
  dispatch(setSelectedStore(null));
  dispatch(clearSession());
  dispatch({ type: 'auth/clearAuth' });
  try {
    await authApi.logout();
  } catch {
    /* no active session */
  }
}

export const registerAccount = createAsyncThunk(
  'auth/register',
  async (body: OrganizationRegisterRequest, { dispatch }) => {
    await clearPriorSessionBeforeSignup(dispatch);
    const data = await authApi.register(body);
    await finishAuthSession(dispatch, data);
    return data;
  },
);

export const addOrgApplication = createAsyncThunk(
  'auth/addApplication',
  async (body: AddApplicationRequest, { dispatch }) => {
    clearWorkspaceScopeStorage();
    dispatch(setSelectedCompany(null));
    dispatch(setSelectedStore(null));
    const data = await authApi.addApplication(body);
    await finishAuthSession(dispatch, data);
    return data;
  },
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: Omit<LoginRequest, 'deviceId'>, { dispatch }) => {
    clearWorkspaceScopeStorage();
    dispatch(setSelectedCompany(null));
    dispatch(setSelectedStore(null));
    const data = await authApi.login({
      ...credentials,
      deviceId: getOrCreateDeviceId(),
    });
    if (!data.mfaRequired) {
      await finishAuthSession(dispatch, data);
    }
    return data;
  },
);

export const verifyMfaLogin = createAsyncThunk(
  'auth/verifyMfa',
  async (payload: { mfaToken: string; totpCode: string; entryModule?: string; masterLogin?: boolean }, { dispatch }) => {
    const data = await authApi.verifyMfa({
      mfaToken: payload.mfaToken,
      totpCode: payload.totpCode,
      deviceId: getOrCreateDeviceId(),
      entryModule: payload.entryModule,
      masterLogin: payload.masterLogin,
    });
    dispatch(setSelectedCompany(null));
    dispatch(setSelectedStore(null));
    await finishAuthSession(dispatch, data);
    return data;
  },
);

export const completeSsoLogin = createAsyncThunk(
  'auth/ssoExchange',
  async (
    payload: { exchangeToken: string; deviceId?: string; entryModule?: string; oauthPictureUrl?: string },
    { dispatch },
  ) => {
    const key = payload.exchangeToken;
    let inflight = inflightSsoByToken.get(key);
    if (!inflight) {
      inflight = (async () => {
        await clearPriorSessionBeforeSignup(dispatch);
        return authApi.ssoExchange({
          exchangeToken: payload.exchangeToken,
          deviceId: payload.deviceId ?? getOrCreateDeviceId(),
          entryModule: payload.entryModule,
        });
      })();
      inflightSsoByToken.set(key, inflight);
      void inflight.finally(() => inflightSsoByToken.delete(key));
    }
    const data = await inflight;
    if (!data.mfaRequired) {
      const merged = payload.oauthPictureUrl && !data.profilePictureUrl
        ? { ...data, profilePictureUrl: payload.oauthPictureUrl }
        : data;
      await finishAuthSession(dispatch, merged, payload.oauthPictureUrl);
    }
    return payload.oauthPictureUrl && !data.profilePictureUrl
      ? { ...data, profilePictureUrl: payload.oauthPictureUrl }
      : data;
  },
);

export const switchWorkspace = createAsyncThunk(
  'auth/switchWorkspace',
  async (module: ErpModule, { dispatch }) => {
    const data = await platformApi.switchWorkspace(module, getOrCreateDeviceId());
    await finishAuthSession(dispatch, data);
    return { data, module };
  },
);

export const logout = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  beginSignOut();
  queryClient.cancelQueries();
  queryClient.clear();
  clearAuthStorage();
  clearEntryModuleStorage();
  clearUsedWorkspaceModules();
  clearLastAllowedPath();
  dispatch(setSelectedCompany(null));
  dispatch(setSelectedStore(null));
  dispatch(clearSession());
  dispatch(clearAuth());

  try {
    await authApi.logout();
  } catch {
    /* session may already be cleared */
  } finally {
    window.setTimeout(() => endSignOut(), 750);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setInitialized: (state) => {
      state.initialized = true;
    },
    hydrateFromLogin: (state, action: PayloadAction<LoginResponse>) => {
      applyLoginPayload(state, action.payload);
    },
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.enabledFeatures = [];
      state.enabledModules = [];
      state.accessibleModules = [];
      state.entryModule = null;
      state.workspaceModule = null;
      state.mfaEnabled = false;
      state.forcePasswordChange = false;
      clearAuthStorage();
      clearEntryModuleStorage();
      clearLastAllowedPath();
    },
    setEnabledModules: (state, action: PayloadAction<string[]>) => {
      state.enabledModules = action.payload;
    },
    setAccessibleModules: (state, action: PayloadAction<string[]>) => {
      state.accessibleModules = action.payload;
    },
    setWorkspaceModule: (state, action: PayloadAction<ErpModule>) => {
      state.workspaceModule = action.payload;
      persistWorkspaceModule(action.payload);
      markWorkspaceModuleUsed(action.payload);
      const owner = isOrgOwner(state.user?.permissions ?? [], state.user?.roleCode);
      if (!owner) {
        state.entryModule = action.payload;
        persistEntryModule(action.payload);
      }
    },
    setActiveModule: (state, action: PayloadAction<ErpModule>) => {
      state.workspaceModule = action.payload;
      persistWorkspaceModule(action.payload);
      const owner = isOrgOwner(state.user?.permissions ?? [], state.user?.roleCode);
      if (!owner) {
        state.entryModule = action.payload;
        persistEntryModule(action.payload);
      }
    },
    setProfilePictureUrl: (state, action: PayloadAction<string | null>) => {
      if (state.user) {
        state.user.profilePictureUrl = action.payload;
        state.user.profilePictureRevision = Date.now();
      }
    },
    setMfaEnabled: (state, action: PayloadAction<boolean>) => {
      state.mfaEnabled = action.payload;
    },
    clearForcePasswordChange: (state) => {
      state.forcePasswordChange = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bootstrapSession.fulfilled, (state, action) => {
        const data = sessionToLoginResponse(action.payload);
        const hadAuth = state.isAuthenticated && state.user != null;
        const previousWorkspace = state.workspaceModule;
        const previousEntry = state.entryModule;

        applyLoginPayload(state, data, { restore: !hadAuth });

        if (hadAuth) {
          const refreshCtx: WorkspaceModuleContext = {
            enabledModules: state.enabledModules,
            accessibleModules: state.accessibleModules,
            permissions: state.user?.permissions ?? [],
            roleCode: state.user?.roleCode,
          };
          if (previousWorkspace && canUseWorkspaceModule(previousWorkspace, refreshCtx)) {
            state.workspaceModule = previousWorkspace;
            persistWorkspaceModule(previousWorkspace);
          } else if (state.workspaceModule && !canUseWorkspaceModule(state.workspaceModule, refreshCtx)) {
            state.workspaceModule = resolveDefaultAccessibleWorkspaceModule(
              refreshCtx.enabledModules,
              refreshCtx.permissions,
              refreshCtx.accessibleModules,
              refreshCtx.roleCode,
            );
            persistWorkspaceModule(state.workspaceModule);
          }
          if (
            previousEntry != null &&
            canUseWorkspaceModule(previousEntry, refreshCtx)
          ) {
            state.entryModule = previousEntry;
            persistEntryModule(previousEntry);
          } else if (
            !isOrgOwner(state.user?.permissions ?? [], state.user?.roleCode) &&
            state.workspaceModule
          ) {
            state.entryModule = state.workspaceModule;
            persistEntryModule(state.entryModule);
          }
        }
      })
      .addCase(bootstrapSession.rejected, (state) => {
        // Ignore stale bootstrap failures when login/SSO already established a session.
        if (state.isAuthenticated && state.user != null) {
          return;
        }
        state.user = null;
        state.isAuthenticated = false;
        state.enabledFeatures = [];
        state.enabledModules = [];
        state.accessibleModules = [];
        state.entryModule = null;
        state.workspaceModule = null;
        state.mfaEnabled = false;
        state.forcePasswordChange = false;
        clearAuthStorage();
        clearEntryModuleStorage();
        clearLastAllowedPath();
      })
      .addCase(registerAccount.fulfilled, (state, action) => {
        applyLoginPayload(state, action.payload);
      })
      .addCase(addOrgApplication.fulfilled, (state, action) => {
        applyLoginPayload(state, action.payload);
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        // finishAuthSession already hydrated via hydrateFromLogin. Re-applying here
        // clears PENDING_ENTRY_MODULE and can briefly default owners to POS.
        if (!action.payload.mfaRequired && !state.isAuthenticated) {
          applyLoginPayload(state, action.payload);
        }
      })
      .addCase(verifyMfaLogin.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyMfaLogin.fulfilled, (state, action) => {
        state.loading = false;
        applyLoginPayload(state, action.payload);
      })
      .addCase(verifyMfaLogin.rejected, (state) => {
        state.loading = false;
      })
      .addCase(completeSsoLogin.fulfilled, (state, action) => {
        if (!action.payload.mfaRequired) {
          applyLoginPayload(state, action.payload);
        }
      })
      .addCase(switchWorkspace.fulfilled, (state, action) => {
        state.loading = false;
        applyLoginPayload(state, action.payload.data);
        state.workspaceModule = action.payload.module;
        persistWorkspaceModule(action.payload.module);
        markWorkspaceModuleUsed(action.payload.module);
      })
      .addCase(switchWorkspace.pending, (state) => {
        state.loading = true;
      })
      .addCase(switchWorkspace.rejected, (state) => {
        state.loading = false;
      })
      .addCase(login.rejected, (state) => {
        state.loading = false;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.enabledFeatures = [];
        state.enabledModules = [];
        state.accessibleModules = [];
        state.entryModule = null;
        state.workspaceModule = null;
        state.mfaEnabled = false;
        state.forcePasswordChange = false;
      })
      .addMatcher(
        (action): action is PayloadAction<SessionInitResponse> =>
          action.type === SESSION_REFRESH_FULFILLED,
        (state, action) => {
          const data = sessionToLoginResponse(action.payload);
          const previousWorkspace = state.workspaceModule;
          const previousEntry = state.entryModule;
          applySessionModuleLists(state, data);
          patchUserFromSessionRefresh(state, data);
          state.forcePasswordChange = data.forcePasswordChange ?? false;

          const refreshCtx: WorkspaceModuleContext = {
            enabledModules: state.enabledModules,
            accessibleModules: state.accessibleModules,
            permissions: state.user?.permissions ?? [],
            roleCode: state.user?.roleCode,
          };
          if (previousWorkspace && canUseWorkspaceModule(previousWorkspace, refreshCtx)) {
            state.workspaceModule = previousWorkspace;
            persistWorkspaceModule(previousWorkspace);
          } else if (state.workspaceModule && !canUseWorkspaceModule(state.workspaceModule, refreshCtx)) {
            state.workspaceModule = resolveDefaultAccessibleWorkspaceModule(
              refreshCtx.enabledModules,
              refreshCtx.permissions,
              refreshCtx.accessibleModules,
              refreshCtx.roleCode,
            );
            persistWorkspaceModule(state.workspaceModule);
          }
          if (previousEntry != null && canUseWorkspaceModule(previousEntry, refreshCtx)) {
            state.entryModule = previousEntry;
            persistEntryModule(previousEntry);
          } else if (
            !isOrgOwner(state.user?.permissions ?? [], state.user?.roleCode) &&
            state.workspaceModule
          ) {
            state.entryModule = state.workspaceModule;
            persistEntryModule(state.entryModule);
          }
        },
      );
  },
});

export const {
  setInitialized,
  hydrateFromLogin,
  clearAuth,
  setEnabledModules,
  setAccessibleModules,
  setWorkspaceModule,
  setActiveModule,
  setProfilePictureUrl,
  setMfaEnabled,
  clearForcePasswordChange,
} = authSlice.actions;
export default authSlice.reducer;
